import { Contact, ContactsSchedule, ContactsScheduleDay } from '@/api';
import { DateTime } from 'luxon';

type DayOfWeek =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';

function isTimeInWindow(
    currentMinutes: number,
    startMinutes: number,
    endMinutes: number
): boolean {
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}

function getDayOfWeek(dateTime: DateTime): DayOfWeek {
    // Luxon weekday: 1 = Monday, 7 = Sunday
    const days: DayOfWeek[] = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday'
    ];
    return days[dateTime.weekday - 1];
}

function isWithinContactsSchedule(contactsSchedule: ContactsSchedule): boolean {
    const defaultTimezone = 'Europe/London';

    const timezone = defaultTimezone;

    const now = DateTime.now().setZone(timezone);

    const currentMinutes = now.hour * 60 + now.minute;
    const dayOfWeek = getDayOfWeek(now);
    const dayConfig: ContactsScheduleDay = contactsSchedule[dayOfWeek];

    // If not a working day, we're outside the schedule
    if (!dayConfig.isWorkingDay) {
        return false;
    }

    // If working all day, we're within the schedule
    if (dayConfig.allDay) {
        return true;
    }

    const startTime = dayConfig.startTime;
    const endTime = dayConfig.endTime;

    // Incomplete configuration - default to within schedule
    if (typeof startTime !== 'number' || typeof endTime !== 'number') {
        return true;
    }

    return isTimeInWindow(currentMinutes, startTime, endTime);
}

export function isCurrentlyOutOfHours(
    contactsSchedule: ContactsSchedule | null,
    outOfHoursEnabled: boolean,
    outOfHoursContacts: Contact[]
): boolean {
    // Cannot be out-of-hours if the feature is disabled
    if (!outOfHoursEnabled) {
        return false;
    }

    // Cannot be out-of-hours without a schedule to determine working hours
    if (!contactsSchedule) {
        return false;
    }

    // Cannot be out-of-hours without out-of-hours contacts configured
    if (outOfHoursContacts.length === 0) {
        return false;
    }

    // Out-of-hours if NOT within the contacts schedule
    return !isWithinContactsSchedule(contactsSchedule);
}
